// PipelineHoldupTool.jsx
// Interactive pipeline liquid holdup calculator
// Fetches elevation data from Alberta API, runs multiphase flow model,
// visualises holdup distribution along the pipeline.

import React, { useState, useRef, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const G   = 9.80665;
const R   = 8.314462;
const P_STD = 101.325;   // kPa
const T_STD = 288.15;    // K

// Critical properties for 10-component system [Tc K, Pc kPa, omega]
const COMP_PROPS = {
  N2:  { Tc: 126.2, Pc: 3394, omega: 0.040, MW: 28.01 },
  CO2: { Tc: 304.2, Pc: 7376, omega: 0.225, MW: 44.01 },
  H2S: { Tc: 373.2, Pc: 8963, omega: 0.100, MW: 34.08 },
  C1:  { Tc: 190.6, Pc: 4600, omega: 0.008, MW: 16.04 },
  C2:  { Tc: 305.4, Pc: 4884, omega: 0.099, MW: 30.07 },
  C3:  { Tc: 369.8, Pc: 4246, omega: 0.152, MW: 44.10 },
  iC4: { Tc: 408.1, Pc: 3648, omega: 0.176, MW: 58.12 },
  nC4: { Tc: 425.2, Pc: 3797, omega: 0.193, MW: 58.12 },
  iC5: { Tc: 460.4, Pc: 3381, omega: 0.227, MW: 72.15 },
  nC5: { Tc: 469.6, Pc: 3369, omega: 0.251, MW: 72.15 },
};

const DEFAULT_COMPOSITION = {
  N2: 0.005, CO2: 0.010, H2S: 0.000,
  C1: 0.850, C2: 0.070, C3: 0.040,
  iC4: 0.008, nC4: 0.012, iC5: 0.003, nC5: 0.002,
};

// ─────────────────────────────────────────────────────────────────────────────
// FLUID PHYSICS (ported from pipeline_holdup_model.py)
// ─────────────────────────────────────────────────────────────────────────────

function mixtureProps(comp) {
  const keys = Object.keys(comp);
  let MW = 0, Tc = 0, Pc = 0, omega = 0, heavy = 0;
  keys.forEach(k => {
    const y = comp[k];
    const p = COMP_PROPS[k];
    if (!p) return;
    MW    += y * p.MW;
    Tc    += y * p.Tc;
    Pc    += y * p.Pc;
    omega += y * p.omega;
    if (['C3','iC4','nC4','iC5','nC5'].includes(k)) heavy += y;
  });
  return { MW, Tc, Pc, omega, heavy };
}

function zFactor(P_kPa, T_K, Tpc, Ppc) {
  const Tpr = T_K / Tpc;
  const Ppr = P_kPa / Ppc;
  // DAK correlation
  const A = [0.3265,-1.0700,-0.5339,0.01569,-0.05165,
             0.5475,-0.7361,0.1844,0.1056,0.6134,0.7210];
  let Z = 1.0;
  for (let i = 0; i < 30; i++) {
    const rr = 0.27 * Ppr / (Z * Tpr);
    const Zn = 1
      + (A[0]+A[1]/Tpr+A[2]/Tpr**3+A[3]/Tpr**4+A[4]/Tpr**5)*rr
      + (A[5]+A[6]/Tpr+A[7]/Tpr**2)*rr**2
      - A[8]*(A[6]/Tpr+A[7]/Tpr**2)*rr**5
      + A[9]*(1+A[10]*rr**2)*(rr**2/Tpr**3)*Math.exp(-A[10]*rr**2);
    if (Math.abs(Zn - Z) < 1e-6) { Z = Zn; break; }
    Z = 0.7*Z + 0.3*Zn;
  }
  return Math.max(0.3, Math.min(1.5, Z));
}

function gasDensity(P_kPa, T_K, MW, Tpc, Ppc) {
  const Z = zFactor(P_kPa, T_K, Tpc, Ppc);
  return (P_kPa * 1000 * MW) / (Z * R * T_K * 1000);  // kg/m³
}

function gasViscosity(P_kPa, T_K, MW, Tpc, Ppc) {
  const rho = gasDensity(P_kPa, T_K, MW, Tpc, Ppc) / 1000; // g/cm³
  const K = (9.4 + 0.02*MW) * T_K**1.5 / (209 + 19*MW + T_K);
  const X = 3.5 + 986/T_K + 0.01*MW;
  const Y = 2.4 - 0.2*X;
  return 1e-7 * K * Math.exp(X * rho**Y);
}

function liquidDensity(T_K, heavy) {
  const API = Math.max(20, 60 - 30*heavy);
  const SG  = 141.5 / (API + 131.5);
  const T_C = T_K - 273.15;
  return SG * 999 * (1 - 0.0008*(T_C*1.8+32-60));
}

// Beggs-Brill: returns [regime, H_L]
function beggsBrill(vsg, vsl, rhoG, rhoL, D_m, incl_deg) {
  if (vsg + vsl < 0.001) return ['stratified', incl_deg < 10 ? 0.5 : 0.0];
  const vm = vsg + vsl;
  const lL = vsl / vm;
  const Fr = vm**2 / (G * D_m);

  const L1 = lL > 0 ? 316  * lL**0.302  : 0;
  const L2 = lL > 0 ? 9.252e-4 * lL**(-2.4684) : 1e10;
  const L3 = lL > 0 ? 0.10 * lL**(-1.4516) : 1e10;
  const L4 = lL > 0 ? 0.5  * lL**(-6.738)  : 1e10;

  let pattern;
  if      (lL <  0.01 && Fr <  L1) pattern = 'segregated';
  else if (lL >= 0.01 && Fr <  L2) pattern = 'segregated';
  else if (lL >= 0.01 && Fr >= L2 && Fr <= L3) pattern = 'transition';
  else if (lL <  0.4  && Fr >= L1) pattern = 'distributed';
  else if (lL >= 0.4  && Fr >  L4) pattern = 'distributed';
  else                              pattern = 'intermittent';

  let a, b, c;
  if      (pattern === 'segregated')  [a,b,c] = [0.98,  0.4846, 0.0868];
  else if (pattern === 'intermittent')[a,b,c] = [0.845, 0.5351, 0.0173];
  else                                [a,b,c] = [1.065, 0.5824, 0.0609];

  let HL0 = lL > 0 ? a * lL**b / Math.max(Fr,0.001)**c : 0;
  HL0 = Math.max(lL, Math.min(1.0, HL0));

  // Inclination correction
  const theta = (incl_deg * Math.PI) / 180;
  let d,e,f,g2;
  if (pattern === 'segregated')   [d,e,f,g2] = [0.011,-3.768, 3.539,-1.614];
  else if (pattern === 'intermittent')[d,e,f,g2] = [2.96, 0.305,-0.4473, 0.0978];
  else                            [d,e,f,g2] = [0,0,0,0];

  const NLv = vsl * (rhoL / (G * 0.02))**0.25;
  let C = 0;
  if (d > 0 && lL > 0.001 && lL < 0.999 && Fr > 0.001) {
    const arg = d * lL**e * Math.max(NLv,0.001)**f * Fr**g2;
    if (arg > 0) C = Math.max(0, (1-lL) * Math.log(arg));
  }
  const psi = incl_deg >= 0
    ? 1 + C*(Math.sin(1.8*theta) - 0.333*Math.sin(1.8*theta)**3)
    : 1 + C*(Math.sin(1.8*theta) + 0.333*Math.sin(1.8*theta)**3);

  const HL = Math.max(0, Math.min(1, HL0 * psi));
  const regimeMap = { segregated:'stratified', transition:'wave', intermittent:'slug', distributed:'dispersed' };
  return [regimeMap[pattern] || 'stratified', HL];
}

function lowSpotAccumulation(vsg, rhoG, rhoL, D_m) {
  const vCrit = 0.7 * Math.sqrt(G * D_m * (rhoL - rhoG) / rhoG);
  if (vsg > vCrit) return 0.1;
  const acc = vCrit > 0 ? (1 - vsg/vCrit)**2 : 1.0;
  return Math.min(0.9, acc);
}

// ─────────────────────────────────────────────────────────────────────────────
// STEADY-STATE SOLVER
// ─────────────────────────────────────────────────────────────────────────────

function solveSteadyState({ elevationPoints, diameterMm, inletPressureKPa,
                             inletTempC, gasFlowE3m3d, liquidFlowM3d, comp }) {
  const mix      = mixtureProps(comp);
  const D_m      = diameterMm / 1000;
  const area     = Math.PI * (D_m/2)**2;
  const roughness = 0.000045; // m
  const q_gas    = gasFlowE3m3d * 1000;   // m³/d std
  const q_liq    = liquidFlowM3d;          // m³/d std

  let P = inletPressureKPa;
  let T = inletTempC + 273.15;

  const results = [];
  const nSeg    = elevationPoints.length - 1;
  const holdupVol = new Array(nSeg).fill(0);

  for (let i = 0; i < nSeg; i++) {
    const pt0 = elevationPoints[i];
    const pt1 = elevationPoints[i+1];
    const segLen   = (pt1.distance - pt0.distance) * 1000; // m
    const dElev    = pt1.elevation - pt0.elevation;        // m
    const inclDeg  = Math.atan2(dElev, segLen) * (180/Math.PI);
    const midKm    = 0.5*(pt0.distance + pt1.distance);
    const midElev  = 0.5*(pt0.elevation + pt1.elevation);
    const segVol   = area * segLen;

    const rhoG = gasDensity(P, T, mix.MW, mix.Tc, mix.Pc);
    const rhoL = liquidDensity(T, mix.heavy);
    const muG  = gasViscosity(P, T, mix.MW, mix.Tc, mix.Pc);

    // Actual volumetric flow
    const Z = zFactor(P, T, mix.Tc, mix.Pc);
    const qG_act = (q_gas/86400) * (P_STD/P) * (T/T_STD) * Z;  // m³/s
    const qL_act = q_liq/86400;

    const vsg = qG_act / area;
    const vsl = qL_act / area;

    let [regime, HL] = beggsBrill(vsg, vsl, rhoG, rhoL, D_m, inclDeg);

    // Low-spot accumulation check
    if (inclDeg < -2 && i < nSeg-1) {
      const next = elevationPoints[i+2];
      if (next) {
        const nextIncl = Math.atan2(next.elevation - pt1.elevation,
                                     (next.distance - pt1.distance)*1000) * (180/Math.PI);
        if (nextIncl > 2) {
          const factor = 1 + lowSpotAccumulation(vsg, rhoG, rhoL, D_m);
          HL = Math.min(0.95, HL * factor);
        }
      }
    }

    const rhoM = HL * rhoL + (1-HL) * rhoG;
    const vm   = vsg + vsl;
    const Re   = rhoM * Math.max(vm, 0.001) * D_m / muG;
    const epsD = roughness / D_m;
    const f    = Re > 2300
      ? 0.25 / (Math.log10(epsD/3.7 + 5.74/Re**0.9))**2
      : 64 / Math.max(Re, 1);

    const dPf = f * segLen * rhoM * vm**2 / (2 * D_m) / 1000;  // kPa
    const dPe = rhoM * G * dElev / 1000;                         // kPa
    const P_out = Math.max(100, P - dPf - dPe);

    const liqVol = HL * segVol;
    holdupVol[i] = liqVol;

    results.push({
      segIdx:       i,
      km:           midKm,
      elevation:    midElev,
      inclDeg,
      pressure_kPa: P,
      temp_C:       T - 273.15,
      vsg, vsl,
      regime,
      liquidHoldup: HL,
      liquidVol_m3: liqVol,
      segVol_m3:    segVol,
    });

    P = P_out;
    T = Math.max(280, T - 0.5*segLen/1000);
  }

  const totalHoldup   = holdupVol.reduce((s,v)=>s+v, 0);
  const maxIdx        = holdupVol.indexOf(Math.max(...holdupVol));
  const inletPressure = inletPressureKPa;
  const outletPressure= results.length ? results[results.length-1].pressure_kPa : inletPressure;

  return {
    segments: results,
    holdupVol,
    totalHoldup_m3:    totalHoldup,
    totalHoldup_bbl:   totalHoldup * 6.29,
    maxHoldup_km:      results[maxIdx]?.km ?? 0,
    maxHoldup_pct:     (results[maxIdx]?.liquidHoldup ?? 0) * 100,
    pressureDrop_kPa:  inletPressure - outletPressure,
    outletPressure_kPa: outletPressure,
    problemSegments:   results.filter(r => r.liquidHoldup > 0.25),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// API FETCH
// ─────────────────────────────────────────────────────────────────────────────

async function fetchPipelineData(pipelineId) {
  const BASE = 'https://sat.albertaog.ca/api';
  const headers = {
    'Accept': '*/*',
    'Origin': 'https://albertaog.ca',
    'Referer': 'https://albertaog.ca/',
  };

  const [detailsRes, elevationRes] = await Promise.all([
    fetch(`${BASE}/details/pipeline/${pipelineId}`, { headers }),
    fetch(`${BASE}/pipeline-elevation/${pipelineId}`,  { headers }),
  ]);

  if (!detailsRes.ok)   throw new Error(`Details fetch failed: ${detailsRes.status}`);
  if (!elevationRes.ok) throw new Error(`Elevation fetch failed: ${elevationRes.status}`);

  const details   = await detailsRes.json();
  const elevation = await elevationRes.json();
  return { details, elevation };
}

// ─────────────────────────────────────────────────────────────────────────────
// ELEVATION CHART (canvas-based, no charting library needed)
// ─────────────────────────────────────────────────────────────────────────────

const ElevationChart = ({ segments, elevationPoints }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !segments?.length) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const pad = { top: 16, right: 20, bottom: 44, left: 60 };
    const cW = W - pad.left - pad.right;
    const cH = H - pad.top  - pad.bottom;

    ctx.clearRect(0, 0, W, H);

    // Elevation profile data
    const kmArr   = elevationPoints.map(p => p.distance);
    const elevArr = elevationPoints.map(p => p.elevation);
    const kmMin   = kmArr[0];
    const kmMax   = kmArr[kmArr.length-1];
    const eMin    = Math.min(...elevArr);
    const eMax    = Math.max(...elevArr);
    const eRange  = Math.max(eMax - eMin, 5);
    const ePad    = eRange * 0.15;

    // Holdup colours per segment band
    const holdupByKm = {};
    segments.forEach(s => { holdupByKm[s.km] = s.liquidHoldup; });

    const toX = km   => pad.left + ((km - kmMin) / (kmMax - kmMin)) * cW;
    const toY = elev => pad.top  + (1 - (elev - (eMin-ePad)) / (eRange+2*ePad)) * cH;

    // ── Draw holdup heatmap bands ──
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const x0  = toX(elevationPoints[i].distance);
      const x1  = toX(elevationPoints[i+1]?.distance ?? kmMax);
      const hl  = seg.liquidHoldup;
      let r,g,b;
      if (hl < 0.1)      { r=22; g=163; b=74; }   // green
      else if (hl < 0.25){ r=234; g=179; b=8; }   // amber
      else if (hl < 0.40){ r=234; g=88; b=12; }   // orange
      else               { r=220; g=38; b=38; }   // red
      ctx.fillStyle = `rgba(${r},${g},${b},${0.18 + hl*0.45})`;
      // Fill from bottom of chart up to a bar proportional to holdup
      const barH = hl * cH * 0.38;
      ctx.fillRect(x0, pad.top + cH - barH, x1-x0, barH);
    }

    // ── Terrain fill ──
    ctx.beginPath();
    ctx.moveTo(toX(kmMin), pad.top + cH);
    elevationPoints.forEach(p => ctx.lineTo(toX(p.distance), toY(p.elevation)));
    ctx.lineTo(toX(kmMax), pad.top + cH);
    ctx.closePath();
    ctx.fillStyle = 'rgba(120,80,40,0.18)';
    ctx.fill();

    // ── Elevation line ──
    ctx.beginPath();
    elevationPoints.forEach((p,i) => {
      if (i===0) ctx.moveTo(toX(p.distance), toY(p.elevation));
      else        ctx.lineTo(toX(p.distance), toY(p.elevation));
    });
    ctx.strokeStyle = '#92400e';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // ── Warning lines ──
    const drawHLine = (hl, colour, label) => {
      const y = pad.top + cH * (1 - hl * 0.38);
      ctx.beginPath();
      ctx.setLineDash([6,4]);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(W - pad.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = colour;
      ctx.font = '10px monospace';
      ctx.fillText(label, W - pad.right - 52, y - 3);
    };
    drawHLine(0.25, '#f59e0b', '25% warn');
    drawHLine(0.40, '#ef4444', '40% crit');

    // ── Axes ──
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    // Y axis (elevation)
    ctx.beginPath();
    ctx.moveTo(pad.left, pad.top);
    ctx.lineTo(pad.left, pad.top + cH);
    ctx.stroke();
    // X axis
    ctx.beginPath();
    ctx.moveTo(pad.left, pad.top + cH);
    ctx.lineTo(W - pad.right, pad.top + cH);
    ctx.stroke();

    // Y labels (elevation)
    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    const nTicks = 5;
    for (let t = 0; t <= nTicks; t++) {
      const frac  = t / nTicks;
      const elev  = (eMin - ePad) + frac * (eRange + 2*ePad);
      const y     = pad.top + cH - frac*cH;
      ctx.fillText(elev.toFixed(0)+'m', pad.left - 4, y + 4);
      ctx.beginPath();
      ctx.strokeStyle = '#374151';
      ctx.moveTo(pad.left, y);
      ctx.lineTo(pad.left + cW, y);
      ctx.lineWidth = 0.3;
      ctx.stroke();
    }

    // X labels (km)
    ctx.textAlign = 'center';
    ctx.fillStyle = '#9ca3af';
    const nXTicks = Math.min(8, Math.ceil(kmMax - kmMin));
    for (let t = 0; t <= nXTicks; t++) {
      const km = kmMin + (t / nXTicks) * (kmMax - kmMin);
      const x  = toX(km);
      ctx.fillText(km.toFixed(1), x, pad.top + cH + 14);
    }
    ctx.fillText('Distance (km)', pad.left + cW/2, pad.top + cH + 30);

    // Axis label
    ctx.save();
    ctx.translate(12, pad.top + cH/2);
    ctx.rotate(-Math.PI/2);
    ctx.fillStyle = '#9ca3af';
    ctx.textAlign = 'center';
    ctx.fillText('Elevation (m)', 0, 0);
    ctx.restore();

  }, [segments, elevationPoints]);

  return (
    <canvas
      ref={canvasRef}
      width={780}
      height={260}
      style={{ width:'100%', height:'auto', display:'block' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HOLDUP BAR CHART
// ─────────────────────────────────────────────────────────────────────────────

const HoldupChart = ({ segments }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !segments?.length) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const pad = { top:16, right:20, bottom:44, left:60 };
    const cW = W - pad.left - pad.right;
    const cH = H - pad.top  - pad.bottom;

    ctx.clearRect(0,0,W,H);

    const bW = cW / segments.length;

    segments.forEach((s, i) => {
      const hl = s.liquidHoldup;
      let r,g,b;
      if (hl < 0.1)      { r=22;  g=163; b=74;  }
      else if (hl < 0.25){ r=234; g=179; b=8;   }
      else if (hl < 0.40){ r=234; g=88;  b=12;  }
      else               { r=220; g=38;  b=38;  }

      const x = pad.left + i * bW;
      const barH = hl * cH;
      ctx.fillStyle = `rgba(${r},${g},${b},0.85)`;
      ctx.fillRect(x, pad.top + cH - barH, bW * 0.85, barH);
    });

    // Warning lines
    [0.25, 0.40].forEach((hl, idx) => {
      const y = pad.top + cH * (1 - hl);
      ctx.beginPath();
      ctx.setLineDash([6,4]);
      ctx.strokeStyle = idx === 0 ? '#f59e0b' : '#ef4444';
      ctx.lineWidth = 1.5;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(W-pad.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = ctx.strokeStyle;
      ctx.font = '10px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(idx === 0 ? '25%' : '40%', W-pad.right-4, y-3);
    });

    // Axes
    ctx.strokeStyle = '#6b7280';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(pad.left, pad.top);
    ctx.lineTo(pad.left, pad.top+cH);
    ctx.lineTo(W-pad.right, pad.top+cH);
    ctx.stroke();

    ctx.fillStyle = '#9ca3af';
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    [0,25,50,75,100].forEach(pct => {
      const y = pad.top + cH*(1-pct/100);
      ctx.fillText(pct+'%', pad.left-4, y+4);
      ctx.beginPath();
      ctx.strokeStyle = '#374151';
      ctx.lineWidth = 0.3;
      ctx.moveTo(pad.left,y);
      ctx.lineTo(pad.left+cW,y);
      ctx.stroke();
    });

    // X ticks (km)
    ctx.fillStyle = '#9ca3af';
    ctx.textAlign = 'center';
    const step = Math.max(1, Math.floor(segments.length / 8));
    segments.forEach((s,i) => {
      if (i % step !== 0) return;
      const x = pad.left + (i+0.5)*bW;
      ctx.fillText(s.km.toFixed(1), x, pad.top+cH+14);
    });
    ctx.fillText('Distance (km)', pad.left+cW/2, pad.top+cH+30);

    ctx.save();
    ctx.translate(12, pad.top+cH/2);
    ctx.rotate(-Math.PI/2);
    ctx.fillStyle = '#9ca3af';
    ctx.textAlign = 'center';
    ctx.fillText('Liquid Holdup', 0, 0);
    ctx.restore();

  }, [segments]);

  return (
    <canvas
      ref={canvasRef}
      width={780}
      height={200}
      style={{ width:'100%', height:'auto', display:'block' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, colour }) => (
  <div style={{
    background:'#111827',
    border:`1px solid ${colour}33`,
    borderRadius:8,
    padding:'14px 18px',
    minWidth:130,
    flex:'1 1 130px',
  }}>
    <div style={{ fontSize:11, color:'#6b7280', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:700, color: colour, fontFamily:'monospace', lineHeight:1 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:'#9ca3af', marginTop:4 }}>{sub}</div>}
  </div>
);

// Regime colour dot
const REGIME_COLOUR = {
  stratified: '#38bdf8',
  wave:       '#818cf8',
  slug:       '#fb923c',
  dispersed:  '#4ade80',
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function PipelineHoldupTool() {
  // ── Inputs ──
  const [pipelineId, setPipelineId]   = useState('103929');
  const [gasFlow, setGasFlow]         = useState('15');      // e3m³/d
  const [liquidFlow, setLiquidFlow]   = useState('0.5');     // m³/d
  const [inletPressure, setInletPressure] = useState('');    // kPa (blank = 80% MAOP)
  const [inletTemp, setInletTemp]     = useState('10');      // °C

  // ── State ──
  const [status, setStatus]     = useState('idle');   // idle | fetching | solving | done | error
  const [error, setError]       = useState('');
  const [pipeInfo, setPipeInfo] = useState(null);     // parsed pipeline metadata
  const [elevPts, setElevPts]   = useState(null);     // [{distance,elevation}]
  const [results, setResults]   = useState(null);     // solveSteadyState output
  const [activeTab, setActiveTab] = useState('profile');

  const handleAnalyze = useCallback(async () => {
    setStatus('fetching');
    setError('');
    setResults(null);
    setPipeInfo(null);
    setElevPts(null);

    try {
      const { details, elevation } = await fetchPipelineData(parseInt(pipelineId, 10));

      const pipelineInfo = elevation.pipeline_info || {};
      const OD   = parseFloat(details['Outside Diameter (mm)']  || pipelineInfo.diameter  || 60.3);
      const WT   = parseFloat(details['Wall Thickness (mm)']    || pipelineInfo.wall_thickness || 2.77);
      const MAOP = parseFloat(details['Maximum Allowable Operating Pressure (kPa)'] || pipelineInfo.max_pressure || 5830);
      const ID   = OD - 2*WT;

      const parsed = {
        id:        details.ID || pipelineInfo.id,
        licence:   details['License Number'] || pipelineInfo.licence_no,
        company:   details['Company Name']   || pipelineInfo.company_name,
        OD, WT, ID, MAOP,
        substance: details['Primary Substance'] || pipelineInfo.substance1 || 'Natural Gas',
        h2s:       parseFloat(details['H2S Content (mol/kmol)'] || 0),
        lengthKm:  pipelineInfo.total_distance_km || 0,
      };
      setPipeInfo(parsed);

      const points = elevation.points || [];
      if (!points.length) throw new Error('No elevation data returned for this pipeline.');
      setElevPts(points);

      setStatus('solving');

      // Select composition based on H2S
      const comp = parsed.h2s > 0.5
        ? { ...DEFAULT_COMPOSITION, H2S: Math.min(0.05, parsed.h2s/1000), C1: DEFAULT_COMPOSITION.C1 - Math.min(0.05, parsed.h2s/1000) }
        : DEFAULT_COMPOSITION;

      // Normalise
      const total = Object.values(comp).reduce((s,v)=>s+v,0);
      const normComp = Object.fromEntries(Object.entries(comp).map(([k,v])=>[k,v/total]));

      const inletP = inletPressure.trim()
        ? parseFloat(inletPressure)
        : 0.8 * parsed.MAOP;

      const res = solveSteadyState({
        elevationPoints:  points,
        diameterMm:       ID,
        inletPressureKPa: inletP,
        inletTempC:       parseFloat(inletTemp),
        gasFlowE3m3d:     parseFloat(gasFlow),
        liquidFlowM3d:    parseFloat(liquidFlow),
        comp:             normComp,
      });

      setResults(res);
      setStatus('done');
    } catch (e) {
      setError(e.message || 'Unknown error');
      setStatus('error');
    }
  }, [pipelineId, gasFlow, liquidFlow, inletPressure, inletTemp]);

  // ── Label helpers ──
  const holdupColour = (pct) => {
    if (pct < 10) return '#4ade80';
    if (pct < 25) return '#facc15';
    if (pct < 40) return '#fb923c';
    return '#f87171';
  };

  return (
    <div style={{
      background: '#0d1117',
      color: '#e5e7eb',
      fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
      minHeight: '100%',
      padding: '28px 24px',
    }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
          <div style={{
            width:8, height:32, background:'linear-gradient(180deg,#38bdf8,#0ea5e9)',
            borderRadius:2
          }}/>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, letterSpacing:'-0.02em', color:'#f9fafb' }}>
            Pipeline Liquid Holdup Analyser
          </h2>
        </div>
        <p style={{ margin:'0 0 0 20px', fontSize:12, color:'#6b7280' }}>
          Alberta Pipeline API  ·  Beggs-Brill multiphase flow  ·  Flanigan terrain correction
        </p>
      </div>

      {/* ── Input Panel ── */}
      <div style={{
        background:'#161b22',
        border:'1px solid #21262d',
        borderRadius:10,
        padding:'20px 24px',
        marginBottom:24,
      }}>
        <div style={{ fontSize:11, color:'#6b7280', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:16 }}>
          Pipeline Parameters
        </div>

        <div style={{ display:'flex', flexWrap:'wrap', gap:16, marginBottom:18 }}>
          {/* Pipeline ID */}
          <div style={{ flex:'1 1 160px' }}>
            <label style={{ fontSize:11, color:'#9ca3af', display:'block', marginBottom:5 }}>
              Pipeline ID (albertaog.ca)
            </label>
            <input
              type="number"
              value={pipelineId}
              onChange={e => setPipelineId(e.target.value)}
              style={inputStyle}
              placeholder="e.g. 103929"
            />
          </div>

          {/* Gas Flow */}
          <div style={{ flex:'1 1 140px' }}>
            <label style={{ fontSize:11, color:'#9ca3af', display:'block', marginBottom:5 }}>
              Gas Flow (e³m³/d)
            </label>
            <input
              type="number"
              value={gasFlow}
              onChange={e => setGasFlow(e.target.value)}
              style={inputStyle}
              placeholder="e.g. 15"
            />
          </div>

          {/* Liquid Flow */}
          <div style={{ flex:'1 1 140px' }}>
            <label style={{ fontSize:11, color:'#9ca3af', display:'block', marginBottom:5 }}>
              Liquid Flow (m³/d)
            </label>
            <input
              type="number"
              value={liquidFlow}
              onChange={e => setLiquidFlow(e.target.value)}
              style={inputStyle}
              placeholder="e.g. 0.5"
            />
          </div>

          {/* Inlet Pressure */}
          <div style={{ flex:'1 1 160px' }}>
            <label style={{ fontSize:11, color:'#9ca3af', display:'block', marginBottom:5 }}>
              Inlet Pressure (kPa) <span style={{ color:'#4b5563' }}>— blank = 80% MAOP</span>
            </label>
            <input
              type="number"
              value={inletPressure}
              onChange={e => setInletPressure(e.target.value)}
              style={inputStyle}
              placeholder="auto"
            />
          </div>

          {/* Inlet Temp */}
          <div style={{ flex:'1 1 120px' }}>
            <label style={{ fontSize:11, color:'#9ca3af', display:'block', marginBottom:5 }}>
              Inlet Temp (°C)
            </label>
            <input
              type="number"
              value={inletTemp}
              onChange={e => setInletTemp(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <button
          onClick={handleAnalyze}
          disabled={status === 'fetching' || status === 'solving'}
          style={{
            background: status === 'fetching' || status === 'solving' ? '#1d4ed8aa' : '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            padding: '10px 28px',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: status === 'fetching' || status === 'solving' ? 'not-allowed' : 'pointer',
            letterSpacing: '0.04em',
            transition: 'background 0.15s',
          }}
        >
          {status === 'fetching' ? '⏳  Fetching API…'
           : status === 'solving' ? '⚙  Solving…'
           : '▶  Run Analysis'}
        </button>
      </div>

      {/* ── Error ── */}
      {status === 'error' && (
        <div style={{
          background:'#1a0a0a', border:'1px solid #7f1d1d',
          borderRadius:8, padding:'14px 18px', marginBottom:24,
          color:'#fca5a5', fontSize:13,
        }}>
          <strong>Error:</strong> {error}
          <div style={{ color:'#6b7280', fontSize:11, marginTop:4 }}>
            Check that the pipeline ID is valid on albertaog.ca and that the browser can reach sat.albertaog.ca (CORS may block this in some environments).
          </div>
        </div>
      )}

      {/* ── Pipeline Info Banner ── */}
      {pipeInfo && (
        <div style={{
          background:'#0c1620', border:'1px solid #1e3a5f',
          borderRadius:8, padding:'12px 18px', marginBottom:20,
          display:'flex', flexWrap:'wrap', gap:'12px 32px', fontSize:12,
        }}>
          <span style={{ color:'#38bdf8', fontWeight:700 }}>{pipeInfo.company}</span>
          <span style={{ color:'#6b7280' }}>Licence {pipeInfo.licence}</span>
          <span>{pipeInfo.OD} mm OD / <strong style={{ color:'#e2e8f0' }}>{pipeInfo.ID.toFixed(1)} mm ID</strong></span>
          <span>MAOP <strong style={{ color:'#e2e8f0' }}>{pipeInfo.MAOP.toLocaleString()} kPa</strong></span>
          <span>{pipeInfo.substance}</span>
          {pipeInfo.lengthKm > 0 && <span>{pipeInfo.lengthKm.toFixed(2)} km</span>}
        </div>
      )}

      {/* ── Results ── */}
      {results && (
        <>
          {/* Summary Stats */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:24 }}>
            <StatCard
              label="Total Holdup"
              value={results.totalHoldup_m3.toFixed(1) + ' m³'}
              sub={results.totalHoldup_bbl.toFixed(0) + ' bbl'}
              colour="#38bdf8"
            />
            <StatCard
              label="Max Holdup"
              value={results.maxHoldup_pct.toFixed(0) + '%'}
              sub={`at km ${results.maxHoldup_km.toFixed(2)}`}
              colour={holdupColour(results.maxHoldup_pct)}
            />
            <StatCard
              label="Pressure Drop"
              value={results.pressureDrop_kPa.toFixed(0) + ' kPa'}
              sub={`out: ${results.outletPressure_kPa.toFixed(0)} kPa`}
              colour="#a78bfa"
            />
            <StatCard
              label="Problem Zones"
              value={results.problemSegments.length}
              sub="> 25% holdup"
              colour={results.problemSegments.length > 0 ? '#fb923c' : '#4ade80'}
            />
            <StatCard
              label="Segments"
              value={results.segments.length}
              sub={`${(pipeInfo?.lengthKm || 0).toFixed(2)} km`}
              colour="#6b7280"
            />
          </div>

          {/* Tabs */}
          <div style={{ display:'flex', gap:4, marginBottom:0 }}>
            {['profile','holdup','table'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: activeTab === tab ? '#1d2d44' : 'transparent',
                  color: activeTab === tab ? '#38bdf8' : '#6b7280',
                  border: activeTab === tab ? '1px solid #1e3a5f' : '1px solid transparent',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '8px 18px',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'profile' ? '📈 Elevation Profile'
                 : tab === 'holdup' ? '💧 Holdup Distribution'
                 : '📋 Segment Table'}
              </button>
            ))}
          </div>

          <div style={{
            background:'#0d1520',
            border:'1px solid #1e3a5f',
            borderRadius: activeTab === 'profile' ? '0 6px 6px 6px' : '6px',
            padding:'20px',
            marginBottom:24,
          }}>
            {activeTab === 'profile' && elevPts && (
              <div>
                <div style={{ fontSize:11, color:'#6b7280', marginBottom:12 }}>
                  Brown line = elevation profile · Coloured bars = holdup severity (green/amber/orange/red)
                </div>
                <ElevationChart segments={results.segments} elevationPoints={elevPts} />
              </div>
            )}

            {activeTab === 'holdup' && (
              <div>
                <div style={{ fontSize:11, color:'#6b7280', marginBottom:12 }}>
                  Liquid holdup fraction at each pipeline segment
                </div>
                <HoldupChart segments={results.segments} />

                {/* Legend */}
                <div style={{ display:'flex', gap:16, marginTop:12, fontSize:11 }}>
                  {[['< 10%','#4ade80'],['10–25%','#facc15'],['25–40%','#fb923c'],['> 40%','#f87171']].map(([label,colour]) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <div style={{ width:12, height:12, background:colour, borderRadius:2 }}/>
                      <span style={{ color:'#9ca3af' }}>{label}</span>
                    </div>
                  ))}
                </div>

                {/* Problem areas */}
                {results.problemSegments.length > 0 && (
                  <div style={{ marginTop:20 }}>
                    <div style={{ fontSize:11, color:'#fb923c', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10 }}>
                      ⚠  Problem Areas (&gt;25% Holdup)
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                      {results.problemSegments.map((s,i) => (
                        <div key={i} style={{
                          background:'#1a0e05',
                          border:'1px solid #431407',
                          borderRadius:6,
                          padding:'8px 14px',
                          display:'flex',
                          gap:24,
                          fontSize:12,
                        }}>
                          <span style={{ color:'#fb923c', minWidth:60 }}>km {s.km.toFixed(2)}</span>
                          <span style={{ color:'#fde68a', minWidth:60 }}>{(s.liquidHoldup*100).toFixed(0)}% holdup</span>
                          <span style={{ color:'#9ca3af', minWidth:60 }}>{s.elevation.toFixed(0)} m elev</span>
                          <span style={{ color: REGIME_COLOUR[s.regime] || '#9ca3af' }}>{s.regime}</span>
                          <span style={{ color:'#6b7280' }}>Δ{s.inclDeg.toFixed(1)}°</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'table' && (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, fontFamily:'monospace' }}>
                  <thead>
                    <tr style={{ color:'#6b7280', borderBottom:'1px solid #21262d' }}>
                      {['km','Elev (m)','Incl (°)','P (kPa)','T (°C)','vsg (m/s)','vsl (m/s)','Regime','Holdup','Liq Vol (m³)'].map(h => (
                        <th key={h} style={{ padding:'6px 10px', textAlign:'right', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.segments.map((s,i) => (
                      <tr
                        key={i}
                        style={{
                          background: s.liquidHoldup > 0.40 ? '#1a0505'
                                    : s.liquidHoldup > 0.25 ? '#1a0e05'
                                    : i%2===0 ? '#0d1117' : '#111827',
                          borderBottom:'1px solid #1f2937',
                        }}
                      >
                        <td style={{ ...tdStyle }}>{s.km.toFixed(2)}</td>
                        <td style={{ ...tdStyle }}>{s.elevation.toFixed(0)}</td>
                        <td style={{ ...tdStyle, color: s.inclDeg > 2 ? '#4ade80' : s.inclDeg < -2 ? '#f87171' : '#9ca3af' }}>
                          {s.inclDeg.toFixed(1)}
                        </td>
                        <td style={{ ...tdStyle }}>{s.pressure_kPa.toFixed(0)}</td>
                        <td style={{ ...tdStyle }}>{s.temp_C.toFixed(1)}</td>
                        <td style={{ ...tdStyle }}>{s.vsg.toFixed(3)}</td>
                        <td style={{ ...tdStyle }}>{s.vsl.toFixed(4)}</td>
                        <td style={{ ...tdStyle, color: REGIME_COLOUR[s.regime] || '#9ca3af' }}>{s.regime}</td>
                        <td style={{
                          ...tdStyle,
                          color: holdupColour(s.liquidHoldup * 100),
                          fontWeight: s.liquidHoldup > 0.25 ? 700 : 400,
                        }}>
                          {(s.liquidHoldup * 100).toFixed(1)}%
                        </td>
                        <td style={{ ...tdStyle }}>{s.liquidVol_m3.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Idle placeholder */}
      {status === 'idle' && (
        <div style={{
          border:'1px dashed #21262d',
          borderRadius:10,
          padding:'48px 24px',
          textAlign:'center',
          color:'#4b5563',
          fontSize:13,
        }}>
          Enter a pipeline ID and flow rates, then click <strong style={{ color:'#6b7280' }}>Run Analysis</strong>
          <br/><br/>
          <span style={{ fontSize:11 }}>
            Example: Pipeline ID <code style={{ color:'#38bdf8' }}>103929</code> — ATCO 60.3mm distribution line, 1.56 km
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  background: '#0d1117',
  border: '1px solid #21262d',
  borderRadius: 6,
  color: '#e5e7eb',
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 13,
  padding: '8px 10px',
  boxSizing: 'border-box',
  outline: 'none',
};

const tdStyle = {
  padding: '5px 10px',
  textAlign: 'right',
  color: '#d1d5db',
  whiteSpace: 'nowrap',
};
